import React, { useState, useContext, useEffect } from 'react';
import { usersPathTo } from '../utils/Api';
import { UserContext } from '../contexts/UserContext';
import { Link, useNavigate } from 'react-router-dom';
import SnackbarContext from '../contexts/SnackbarContext';
import TextField from '@mui/material/TextField';
import { Box, Typography, Card, Button } from '@mui/material';
import TudoPetsLogo from '../images/tp_logo_laranja.png';

/*Icons*/
import EmailIcon from '@mui/icons-material/Email';
import KeyIcon from '@mui/icons-material/Key';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';
import { layoutCardRadius } from '../theme/colors';
import GoogleAuthButton from './GoogleAuthButton';
import { claimPendingCoupon } from '../utils/coupons';
import PageHeader from './building_blocks/PageHeader';

/* Padrão Campos Registro */
const FieldLogin = ({ label, icon, type = 'text', value, onChange, placeholder, onKeyPress, name, showPasswordToggle = false, onTogglePassword, showPassword }) => {

  const getIcon = () => {
    if (icon) return icon;
    // Fallback para ícones padrão baseado no tipo
    if (type === 'email') return <EmailIcon sx={{ fontSize: 16 }} />;
    if (type === 'password') return <KeyIcon sx={{ fontSize: 16 }} />;
    return null;
  };

  return (
    /* Login/Email */
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, alignSelf: 'stretch' }}>
      {/* Título Campo */}
      <Typography sx={{ color: 'text.primary', fontFamily: 'Nunito', fontSize: '14px', fontWeight: 700, lineHeight: '14px' }}>
        {label}
      </Typography>

      {/* TextField */}
      <Box sx={{ position: 'relative', width: '100%' }}>
        <TextField
          placeholder={placeholder}
          name={name}
          variant="outlined"
          type={showPasswordToggle && showPassword ? 'text' : type}
          value={value}
          onChange={onChange}
          onKeyPress={onKeyPress}
          fullWidth
          InputProps={{
            sx: {
              bgcolor: 'surface.input',
              borderRadius: layoutCardRadius,
              /* Estilo do input */
              '& .MuiOutlinedInput-input': {
                paddingLeft: '45px !important',
                paddingRight: showPasswordToggle ? '45px !important' : '14px',
                zIndex: 5,

                /* Borda do autofill */
                '&:-webkit-autofill': {
                  borderRadius: `${layoutCardRadius} !important`,
                  zIndex: 1,
                },
              },

              /* Campo preenchido */
              '& .MuiOutlinedInput-notchedOutline': {
                borderWidth: '1px',
                borderRadius: `${layoutCardRadius} !important`,
                zIndex: 2,
              },
            }
          }}
        />

        {/* Icone */}
        <Box sx={{ /* zIndex = 10 alta prioridade */
          position: 'absolute',
          left: '16px',
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
        }}>
          {getIcon()}
        </Box>

        {/* Botão de ver senha*/}
        {showPasswordToggle && (
          <IconButton
            onClick={onTogglePassword}
            sx={{
              position: 'absolute',
              right: '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              color: 'text.secondary',
              padding: '4px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              }
            }}
          >
            {showPassword ? <VisibilityOffIcon sx={{ fontSize: 16 }} /> : <VisibilityIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { setCurrentUser } = useContext(UserContext);
  const { openSnackbar } = useContext(SnackbarContext);
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');

  /*Mostrar/ocultar senha*/
  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  /*Mostrar/ocultar confirmar senha*/
  const handleToggleConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  useEffect(() => {
    document.title = 'Cadastre-se - TudoPets.app';
  }, []);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
  };

  // todo: Melhorar cadastro para que quando sucesso, redirecione para a tela interna do app
  const handleSubmit = (e) => {
    e.preventDefault();

    // Verificar se as senhas são iguais
    if (password !== confirmPassword) {
      openSnackbar("As senhas estão diferentes", "error");
      return;
    }

    usersPathTo.signup({ email: email, password: password }).then(async result => {
      setCurrentUser(result.data);
      localStorage.setItem('current_user', JSON.stringify(result.data));
      const couponResult = await claimPendingCoupon();
      if (couponResult.claimed) {
        const name = couponResult.coupon?.name;
        openSnackbar(name ? `Cadastro feito. Cupom ${name} resgatado!` : 'Cadastro feito. Cupom resgatado!', 'success');
      } else {
        openSnackbar("Cadastro realizado com sucesso!", "success");
        if (couponResult.error) {
          openSnackbar(couponResult.error, "error");
        }
      }
      navigate("/login"); // /login -> /profile
    }, error => {
      const errors = error.response.data.errors;
      const errorMessages = Object.values(errors).flat().join('. ');
      openSnackbar(errorMessages, "error");
    })
  };

  const containerFlex = {
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'stretch'
  }

  const columnCentered = {
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'stretch',
    gap: 2,
    alignItems: 'center'
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', alignContent: 'center', justifyContent: 'center', width: '100%' }}>
      <Card sx={{ display: 'flex', padding: '24px', flexDirection: 'column', alignItems: 'center', gap: 4, maxWidth: '400px', width: '100%', mt: 4 }}>
        <Box sx={{ ...containerFlex, alignItems: 'flex-start', gap: 6 }}>

          <PageHeader title="Crie uma conta" onBack={() => window.history.back()} />

          {/* Logo */}
          <Box sx={{ ...columnCentered }}>
            <Box component="img" src={TudoPetsLogo} alt="Logo TudoPets"
              sx={{ width: '100%', height: 'auto', p: 2 }} />
          </Box>

          <Box sx={{ ...columnCentered }}>
            {/* Email */}
            <FieldLogin
              label="Email"
              type="email"
              value={email}
              onChange={handleEmailChange}
              placeholder="Digite seu email"
              name="email"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSubmit(e);
                }
              }} />

            {/* Senha */}
            <FieldLogin
              label="Senha"
              type="password"
              value={password}
              onChange={handlePasswordChange}
              placeholder=""
              name="password"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSubmit(e);
                }
              }}
              showPasswordToggle={true}
              showPassword={showPassword}
              onTogglePassword={handleTogglePassword}
            />

            {/* Confirmação Senha */}
            <FieldLogin
              label="Confirmar senha"
              type="password"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              placeholder=""
              name="confirmPassword"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSubmit(e);
                }
              }}
              showPasswordToggle={true}
              showPassword={showConfirmPassword}
              onTogglePassword={handleToggleConfirmPassword}
            />
          </Box>

          {/* Login e Criar conta */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, alignSelf: 'stretch' }}>

            {/* Criar conta*/}
            <Button variant="pillContained" color="secondary" onClick={handleSubmit} fullWidth>
              Criar conta
            </Button>

            <GoogleAuthButton />

            {/* Ir para o login */}
            <Typography>
              Já possui uma conta? {' '}
              <Button
                onClick={() => navigate('/login')}
                sx={{
                  textTransform: 'none',
                  p: 0,
                  minWidth: 'auto',
                  verticalAlign: 'baseline',
                  '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' }
                }}
              >
                <Typography sx={{ textDecorationLine: 'underline', fontWeight: 700, color: 'primary.main' }}>
                  Faça o login
                </Typography>
              </Button>
            </Typography>
          </Box>
        </Box>
      </Card>
    </Box>
    // <div>
    //   <h2>Cadastrar</h2>
    //   <form onSubmit={handleSubmit}>
    //     <div>
    //       <label>Email:</label>
    //       <input type="email" value={email} onChange={handleEmailChange} />
    //     </div>
    //     <div>
    //       <label>Password:</label>
    //       <input type="password" value={password} onChange={handlePasswordChange} />
    //     </div>
    //     <Button variant="contained" type="submit">Cadastrar</Button>
    //   </form>
    // </div>
  );
};

export default Register;
